import React, { useState } from 'react';
import { 
    CheckCircle2, X, Sparkles, Store, Building2, 
    Globe2, ArrowRight, HelpCircle, ChevronDown,
    Zap, Shield, Server, CreditCard
} from 'lucide-react';

const PricingPlans = () => {
    const [isAnnual, setIsAnnual] = useState(true);
    const [activePlan, setActivePlan] = useState(1); // Default to Professional
    const [openFaq, setOpenFaq] = useState(0);

    const plans = [
        {
            id: 'starter',
            name: "Starter",
            target: "Single Location",
            icon: Store,
            description: "Perfect for independent restaurants starting their digital journey.",
            priceMonthly: 79,
            priceAnnual: 59,
            badge: null,
            color: "emerald",
            features: [
                "Cloud POS System",
                "Basic HR & Staff Management",
                "Standard Expense Ledger",
                "End-of-day Email Reports",
                "Standard Support"
            ],
            missing: [
                "Multi-Branch Headquarters",
                "Geofenced Time Clocks",
                "Live Kitchen Display (KDS)",
                "Recipe Matrix & Auto-Inventory",
                "True Net Profit ROI Engine"
            ],
            cta: "Start 14-Day Free Trial"
        },
        {
            id: 'pro',
            name: "Professional",
            target: "Multi-Branch",
            icon: Building2,
            description: "The complete operating system for growing restaurant chains.",
            priceMonthly: 149,
            priceAnnual: 119,
            badge: "Most Popular",
            color: "blue",
            features: [
                "Everything in Starter",
                "Multi-Branch Headquarters",
                "Geofenced Time Clocks",
                "Live Kitchen Display (KDS)",
                "Recipe Matrix & Auto-Inventory",
                "True Net Profit ROI Engine",
                "Priority 24/7 Support"
            ],
            missing: [
                "Custom API Integrations",
                "White-label Branding"
            ],
            cta: "Get Started Now"
        },
        {
            id: 'enterprise',
            name: "Enterprise",
            target: "Global Empires",
            icon: Globe2,
            description: "Custom-built architecture for massive scale and franchise operations.",
            priceMonthly: "Custom",
            priceAnnual: "Custom",
            badge: "Unlimited",
            color: "purple",
            features: [
                "Unlimited Branches & Staff",
                "Custom API Integrations",
                "White-label Branding Options",
                "Dedicated Success Manager",
                "On-premise Deployment Options",
                "Custom Feature Development",
                "SLA Guarantee"
            ],
            missing: [],
            cta: "Contact Sales"
        }
    ];

    const faqs = [
        {
            question: "Do I need to buy special hardware?",
            answer: "No. Jilapi is a cloud-native platform. You can run our POS and Kitchen Display Systems on any standard iPad, Android tablet, or web browser."
        },
        {
            question: "How does the multi-branch billing work?",
            answer: "For the Professional plan, the base fee covers your headquarters and your first branch. Each additional branch is billed at a heavily discounted rate of $49/mo."
        },
        {
            question: "Can I cancel or change my plan at any time?",
            answer: "Absolutely. We don't believe in holding your data hostage. You can upgrade, downgrade, or cancel your subscription at any time right from your billing dashboard."
        }
    ];

    // Helper to get color classes based on active plan
    const getColorClasses = (colorStr) => {
        const maps = {
            emerald: { 
                text: 'text-emerald-400', bg: 'bg-emerald-500', border: 'border-emerald-500/30', 
                glow: 'shadow-[0_0_30px_rgba(16,185,129,0.3)]', gradient: 'from-emerald-900/40 to-slate-950',
                accent: 'bg-emerald-500/10 text-emerald-400'
            },
            blue: { 
                text: 'text-blue-400', bg: 'bg-blue-600', border: 'border-blue-500/30', 
                glow: 'shadow-[0_0_30px_rgba(37,99,235,0.3)]', gradient: 'from-blue-900/40 to-slate-950',
                accent: 'bg-blue-500/10 text-blue-400'
            },
            purple: { 
                text: 'text-purple-400', bg: 'bg-purple-600', border: 'border-purple-500/30', 
                glow: 'shadow-[0_0_30px_rgba(147,51,234,0.3)]', gradient: 'from-purple-900/40 to-slate-950',
                accent: 'bg-purple-500/10 text-purple-400'
            }
        };
        return maps[colorStr] || maps.blue;
    };

    const currentPlan = plans[activePlan];
    const theme = getColorClasses(currentPlan.color);
    const isCustom = typeof currentPlan.priceMonthly === 'string';
    const activePrice = isAnnual ? currentPlan.priceAnnual : currentPlan.priceMonthly;

    return (
        <div className="min-h-screen bg-slate-950 font-sans selection:bg-blue-500/30 pb-24 overflow-hidden">
            
            {/* --- TOP HERO SECTION --- */}
            <div className="relative pt-32 pb-32 lg:pt-40 lg:pb-40 overflow-hidden">
                {/* Stunning Background Image & Overlays matching other pages */}
                <div 
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-40 transition-transform duration-1000 scale-105"
                    style={{ backgroundImage: "url('https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?q=80&w=2000&auto=format&fit=crop')" }} // Moody upscale restaurant
                ></div>
                <div className="absolute inset-0 z-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/40"></div>
                
                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-md border border-slate-700 text-blue-400 text-xs font-bold uppercase tracking-widest mb-8 shadow-xl">
                        <CreditCard size={14} className="text-blue-500" /> Transparent Pricing
                    </div>
                    
                    <div className="max-w-4xl mx-auto mb-12">
                        <h1 className="text-4xl md:text-5xl lg:text-7xl font-black text-white tracking-tight leading-[1.1] mb-6 drop-shadow-lg">
                            Simple pricing for <br className="hidden md:block" />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                                serious operations.
                            </span>
                        </h1>
                        <p className="text-lg md:text-xl text-slate-300 font-medium max-w-2xl mx-auto leading-relaxed drop-shadow">
                            One platform to run your entire restaurant empire. Select a tier below to see exactly what you get. No hidden fees, ever.
                        </p>
                    </div>
                </div>
            </div>

            {/* --- FLOATING TOGGLE DOCK --- */}
            <div className="relative z-30 flex justify-center -mt-20 mb-16 px-4">
                <div className="bg-slate-900/80 backdrop-blur-2xl border border-slate-700/80 rounded-full p-2 flex items-center shadow-2xl shadow-black/50">
                    <button 
                        onClick={() => setIsAnnual(false)}
                        className={`px-6 py-3 rounded-full text-sm font-bold transition-all duration-300 ${
                            !isAnnual ? 'bg-slate-800 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'
                        }`}
                    >
                        Monthly Billing
                    </button>
                    <button 
                        onClick={() => setIsAnnual(true)}
                        className={`px-6 py-3 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${
                            isAnnual ? 'bg-slate-800 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'
                        }`}
                    >
                        Annual Billing
                        <span className="bg-emerald-500/20 text-emerald-400 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-500/20">
                            Save 20%
                        </span>
                    </button>
                </div>
            </div>

            {/* --- THE INTERACTIVE CONSOLE CARD --- */}
            <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-20">
                
                {/* The Main Console Container - Glassmorphism */}
                <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-[2.5rem] p-4 lg:p-6 shadow-2xl flex flex-col lg:flex-row gap-6">
                    
                    {/* LEFT SIDE: Navigation Tabs (Hardware button style) */}
                    <div className="w-full lg:w-1/3 flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 custom-scrollbar">
                        {plans.map((plan, idx) => {
                            const isActive = activePlan === idx;
                            const planTheme = getColorClasses(plan.color);
                            
                            return (
                                <button
                                    key={plan.id}
                                    onClick={() => setActivePlan(idx)}
                                    className={`relative flex-shrink-0 lg:flex-shrink w-64 lg:w-full text-left p-6 rounded-3xl transition-all duration-300 border ${
                                        isActive 
                                            ? `bg-slate-800/80 border-slate-700 shadow-xl scale-[1.02] lg:scale-100` 
                                            : `bg-transparent border-transparent hover:bg-slate-800/40 hover:border-slate-700/50`
                                    }`}
                                >
                                    {/* Active Indicator Glowing Dot */}
                                    {isActive && (
                                        <div className={`absolute left-6 top-6 w-2 h-2 rounded-full ${planTheme.bg} shadow-[0_0_10px_currentColor] hidden lg:block`}></div>
                                    )}

                                    <div className="flex items-start justify-between mb-4 lg:pl-6">
                                        <div className={`p-3 rounded-2xl transition-colors ${isActive ? planTheme.accent : 'bg-slate-900 text-slate-500'}`}>
                                            <plan.icon size={24} />
                                        </div>
                                        {plan.badge && (
                                            <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border transition-colors ${
                                                isActive ? planTheme.accent + ' ' + planTheme.border : 'bg-slate-900 border-slate-800 text-slate-500'
                                            }`}>
                                                {plan.badge}
                                            </span>
                                        )}
                                    </div>
                                    <div className="lg:pl-6">
                                        <h3 className={`text-xl font-black tracking-tight mb-1 transition-colors ${isActive ? 'text-white' : 'text-slate-400'}`}>
                                            {plan.name}
                                        </h3>
                                        <p className={`text-sm font-medium transition-colors ${isActive ? planTheme.text : 'text-slate-600'}`}>
                                            {plan.target}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* RIGHT SIDE: Dynamic Details Screen */}
                    <div className={`w-full lg:w-2/3 border rounded-[2rem] p-8 lg:p-12 relative overflow-hidden flex flex-col transition-colors duration-500 bg-gradient-to-br ${theme.gradient} ${theme.border}`}>
                        
                        {/* Subtle inner ambient glow */}
                        <div className={`absolute top-0 right-0 w-96 h-96 blur-[120px] rounded-full pointer-events-none opacity-20 transition-colors duration-500 ${theme.bg}`}></div>

                        {/* Top Info Area */}
                        <div className="relative z-10 flex flex-col sm:flex-row sm:items-end justify-between gap-8 mb-10 pb-10 border-b border-slate-700/50">
                            <div className="max-w-md">
                                <h2 className="text-3xl lg:text-4xl font-black text-white tracking-tight mb-4 flex items-center gap-3">
                                    {currentPlan.name}
                                </h2>
                                <p className="text-slate-300 leading-relaxed text-lg">
                                    {currentPlan.description}
                                </p>
                            </div>

                            <div className="text-left sm:text-right shrink-0">
                                <div className="flex items-baseline sm:justify-end gap-1 mb-2">
                                    {isCustom ? (
                                        <span className="text-5xl font-black text-white tracking-tight">Custom</span>
                                    ) : (
                                        <>
                                            <span className="text-3xl font-bold text-slate-400">$</span>
                                            <span className="text-6xl font-black text-white tracking-tight">{activePrice}</span>
                                            <span className="text-slate-400 font-medium">/mo</span>
                                        </>
                                    )}
                                </div>
                                {!isCustom && (
                                    <div className="text-sm font-bold text-slate-400 h-5">
                                        {isAnnual ? `Billed $${activePrice * 12} annually` : 'Billed month-to-month'}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Feature Checklist Area */}
                        <div className="relative z-10 flex-1">
                            <div className="flex items-center gap-3 mb-8">
                                <Server size={18} className={theme.text} />
                                <h4 className="text-xs font-black text-slate-300 uppercase tracking-widest">Included Core Features</h4>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 mb-12">
                                {/* Included Features */}
                                {currentPlan.features.map((feature, i) => (
                                    <div key={`inc-${i}`} className="flex items-start gap-3">
                                        <div className={`mt-0.5 p-1 rounded-full bg-slate-900 border border-slate-700 ${theme.text}`}>
                                            <CheckCircle2 size={16} />
                                        </div>
                                        <span className="text-slate-100 font-medium leading-relaxed">{feature}</span>
                                    </div>
                                ))}

                                {/* Missing Features (Grayed out) */}
                                {currentPlan.missing.map((feature, i) => (
                                    <div key={`miss-${i}`} className="flex items-start gap-3 opacity-40">
                                        <div className="mt-0.5 p-1 rounded-full bg-slate-900 border border-slate-800 text-slate-500">
                                            <X size={16} />
                                        </div>
                                        <span className="text-slate-400 font-medium line-through decoration-slate-600 leading-relaxed">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CTA Button */}
                        <button className={`relative z-10 w-full py-5 rounded-2xl font-black text-lg flex items-center justify-center gap-3 transition-all duration-300 shadow-xl group text-white ${theme.bg} ${theme.glow} hover:-translate-y-1`}>
                            {currentPlan.cta}
                            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                </div>
            </div>

            {/* --- FAQ SECTION --- */}
            <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-32">
                <div className="text-center mb-16">
                    <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-blue-500/10 text-blue-400 mb-6 border border-blue-500/20">
                        <HelpCircle size={24} />
                    </div>
                    <h2 className="text-3xl font-black text-white mb-4 tracking-tight">Frequently Asked Questions</h2>
                    <p className="text-slate-400 text-lg">Everything you need to know about billing and deployment.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {faqs.map((faq, idx) => (
                        <div 
                            key={idx} 
                            className="bg-slate-900/40 backdrop-blur-sm border border-slate-800/80 rounded-3xl p-8 hover:bg-slate-900/80 transition-colors"
                        >
                            <h4 className="text-white font-bold text-lg mb-4 leading-snug">{faq.question}</h4>
                            <p className="text-slate-400 leading-relaxed">
                                {faq.answer}
                            </p>
                        </div>
                    ))}
                    
                    {/* Final contact nudge Box */}
                    <div className="bg-gradient-to-br from-blue-900/30 to-slate-900/40 backdrop-blur-sm border border-blue-500/20 rounded-3xl p-8 flex flex-col justify-center items-start">
                        <h4 className="text-white font-bold text-lg mb-2">Still have questions?</h4>
                        <p className="text-slate-400 mb-6 leading-relaxed">Our enterprise deployment team is available 24/7 to help you out.</p>
                        <button className="text-blue-400 font-bold hover:text-blue-300 flex items-center gap-2 group transition-colors">
                            Contact Sales Team
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { height: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }
            `}</style>

        </div>
    );
};

export default PricingPlans;